import type { TimetableGeneration, TimetablePeriod, TimetableRequest, TimetableSlot } from "../../services/timetableService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The timetable registers -- periods, teaching assignments, lessons,
 * generations and change requests -- and the wording they share.
 */

export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const dayName = (day: number) => DAYS[day] ?? `Day ${day + 1}`;
export const clock = (time?: string | null) => (time ? time.slice(0, 5) : "");
export const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};
export const periodTime = (period: Pick<TimetablePeriod, "start_time" | "end_time">) => `${clock(period.start_time)}–${clock(period.end_time)}`;
export const periodMinutes = (period: Pick<TimetablePeriod, "start_time" | "end_time">) => toMinutes(period.end_time) - toMinutes(period.start_time);

export type Assignment = {
  id: number;
  teacher: number;
  teacher_name: string;
  subject: number;
  subject_name: string;
  class_assigned: number;
  class_name: string;
  academic_year: number;
  assigned_by_name?: string;
};

// --- Periods -------------------------------------------------------------------------

export const PERIOD_KINDS: [TimetablePeriod["kind"], string, string][] = [
  ["teaching", "Lesson", "Each class has its own lesson, in its own room."],
  ["break", "Break", "Every class breaks together."],
  ["lunch", "Lunch", "Every class eats together."],
  ["sports", "Sports and games", "After classes, for the whole school."],
];
export const kindLabel = (kind: string) => PERIOD_KINDS.find(([value]) => value === kind)?.[1] ?? kind;
export const isLesson = (period: Pick<TimetablePeriod, "kind">) => period.kind === "teaching";

/** "Baby", "Middle Class", "Top", "KG2"... -- the classes that go home at 1 pm. */
export const isNurseryLevel = (level: string) => /^(baby|middle|top)(\s+class)?$|^kg\s*[1-3]$/i.test(level.trim());

/** Case- and spacing-blind, so "Baby Class" in a period matches a "baby class" class. */
export const levelExcluded = (period: Pick<TimetablePeriod, "excluded_levels">, level?: string | null) => {
  const key = (value: string) => value.toLowerCase().split(/\s+/).filter(Boolean).join(" ").replace(/ class$/, "");
  return Boolean(level) && (period.excluded_levels ?? []).some((item) => key(item) === key(level!));
};

const addMinutes = (time: string, minutes: number) => {
  const total = toMinutes(time) + minutes;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

/** The distinct class levels among the year's classes, nursery first. */
export function classLevels(classes: { level?: string }[] | undefined) {
  const rank = (level: string) => {
    const nursery = ["baby", "middle", "top"].findIndex((name) => level.toLowerCase().startsWith(name));
    if (nursery >= 0) return nursery;
    const number = /^(p|s)\s*(\d+)/i.exec(level);
    return number ? (number[1].toLowerCase() === "p" ? 10 : 20) + Number(number[2]) : 100;
  };
  const levels = [...new Set((classes ?? []).map((item) => item.level?.trim()).filter((level): level is string => Boolean(level)))];
  return levels.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

export type DayPlan = Pick<TimetablePeriod, "name" | "kind" | "start_time" | "end_time" | "excluded_levels">;

/**
 * The Ugandan primary school day: two lessons 8:00-10:30, break, two lessons
 * 11:00-1:00, lunch, two lessons from 2:00 to 4:30 or 5:00 -- every class in
 * its own room at once -- then sports if the school has it. The nursery
 * (Baby, Middle, Top) goes home at 1 pm, so the afternoon is not theirs.
 */
export function ugandaSchoolDay({ afternoonEnds, sports, nurseryLevels }: { afternoonEnds: "16:30" | "17:00"; sports: boolean; nurseryLevels: string[] }): DayPlan[] {
  const afternoon = (toMinutes(afternoonEnds) - toMinutes("14:00")) / 2;
  const lesson = (name: string, start: string, end: string, excluded: string[] = []): DayPlan => ({ name, kind: "teaching", start_time: start, end_time: end, excluded_levels: excluded });
  const day: DayPlan[] = [
    lesson("Period 1", "08:00", "09:15"),
    lesson("Period 2", "09:15", "10:30"),
    { name: "Break", kind: "break", start_time: "10:30", end_time: "11:00", excluded_levels: [] },
    lesson("Period 3", "11:00", "12:00"),
    lesson("Period 4", "12:00", "13:00"),
    { name: "Lunch", kind: "lunch", start_time: "13:00", end_time: "14:00", excluded_levels: [] },
    lesson("Period 5", "14:00", addMinutes("14:00", afternoon), nurseryLevels),
    lesson("Period 6", addMinutes("14:00", afternoon), afternoonEnds, nurseryLevels),
  ];
  if (sports) day.push({ name: "Sports", kind: "sports", start_time: afternoonEnds, end_time: addMinutes(afternoonEnds, 30), excluded_levels: nurseryLevels });
  return day;
}

export type PeriodRow = TimetablePeriod & { lessonCount: number };

export const withLessonCounts = (periods: TimetablePeriod[] | undefined, slots: TimetableSlot[] | undefined): PeriodRow[] | undefined =>
  periods?.map((period) => ({ ...period, lessonCount: (slots ?? []).filter((slot) => slot.period === period.id).length }));

/** Gaps between one period's end and the next one's start, e.g. an unrecorded break. */
export function periodGaps(periods: TimetablePeriod[]) {
  const sorted = [...periods].sort((a, b) => a.start_time.localeCompare(b.start_time));
  return sorted.slice(1).flatMap((period, index) => {
    const before = sorted[index];
    const gap = toMinutes(period.start_time) - toMinutes(before.end_time);
    return gap > 0 ? [{ after: before.name, before: period.name, minutes: gap }] : [];
  });
}

export const periodConfig: RegisterConfig<PeriodRow> = {
  search: (row) => row.name,
  views: [
    { key: "all", label: "All" },
    { key: "teaching", label: "Lessons", match: (row) => row.kind === "teaching" },
    { key: "shared", label: "Break, lunch and sports", match: (row) => row.kind !== "teaching" },
  ],
  sorts: [
    { key: "order", label: "Order in the day", compare: byNumber<PeriodRow>((row) => row.order) },
    { key: "start", label: "Start time", compare: byText<PeriodRow>((row) => row.start_time) },
  ],
};

// --- Teaching assignments ----------------------------------------------------------

export const assignmentConfig: RegisterConfig<Assignment> = {
  search: (row) => [row.teacher_name, row.subject_name, row.class_name].join(" "),
  views: [{ key: "all", label: "All" }],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "teacher", label: "Teacher (A–Z)", compare: byText<Assignment>((row) => `${row.teacher_name} ${row.subject_name} ${row.class_name}`) },
    { key: "class", label: "Class", compare: byText<Assignment>((row) => `${row.class_name} ${row.subject_name}`) },
    { key: "subject", label: "Subject (A–Z)", compare: byText<Assignment>((row) => `${row.subject_name} ${row.class_name}`) },
  ],
};

/** Lessons a week per teacher, from the generated slots: who carries the most. */
export function teacherLoads(slots: TimetableSlot[]) {
  const loads = new Map<number, { teacher: number; name: string; lessons: number; classes: Set<string> }>();
  for (const slot of slots) {
    const entry = loads.get(slot.teacher) ?? { teacher: slot.teacher, name: slot.teacher_name, lessons: 0, classes: new Set<string>() };
    entry.lessons += 1;
    entry.classes.add(slot.class_name);
    loads.set(slot.teacher, entry);
  }
  return [...loads.values()].sort((a, b) => b.lessons - a.lessons || a.name.localeCompare(b.name));
}

// --- Lessons -----------------------------------------------------------------------------

export const slotOrder = (periods: Map<number, TimetablePeriod>) => (slot: TimetableSlot) =>
  slot.day_of_week * 10_000 + (periods.get(slot.period) ? toMinutes(periods.get(slot.period)!.start_time) : 0);

export const lessonConfig = (periods: Map<number, TimetablePeriod>): RegisterConfig<TimetableSlot> => ({
  search: (row) => [row.class_name, row.subject_name, row.teacher_name, row.room, row.period_name, dayName(row.day_of_week)].join(" "),
  views: [
    { key: "all", label: "All" },
    ...DAYS.slice(0, 5).map((day, index) => ({ key: `d${index}`, label: day.slice(0, 3), match: (row: TimetableSlot) => row.day_of_week === index })),
  ],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "week", label: "Day and time", compare: byNumber<TimetableSlot>(slotOrder(periods)) },
    { key: "class", label: "Class", compare: (a, b) => a.class_name.localeCompare(b.class_name) || slotOrder(periods)(a) - slotOrder(periods)(b) },
    { key: "teacher", label: "Teacher", compare: (a, b) => a.teacher_name.localeCompare(b.teacher_name) || slotOrder(periods)(a) - slotOrder(periods)(b) },
  ],
});

// --- Generations -----------------------------------------------------------------------

export const generationStatus = (generation: Pick<TimetableGeneration, "status" | "is_active">) => {
  if (generation.is_active) return { label: "In use", dot: "bg-emerald-500" };
  const dots: Record<string, string> = { completed: "bg-gray-400", incomplete: "bg-amber-500", failed: "bg-red-500", generating: "bg-sky-500", pending: "bg-sky-500", optimizing: "bg-sky-500" };
  return { label: humanise(generation.status), dot: dots[generation.status] ?? "bg-gray-400" };
};

export const generationConfig: RegisterConfig<TimetableGeneration> = {
  search: (row) => [row.name, row.generated_by_name, row.status].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "In use", match: (row) => row.is_active },
    { key: "problems", label: "With problems", match: (row) => row.conflicts_found > 0 || row.status === "failed" || row.status === "incomplete" },
  ],
  date: { label: "Generated", value: (row) => row.created_at },
  sorts: [{ key: "-created", label: "Newest first", compare: reversed(byText<TimetableGeneration>((row) => row.created_at)) }],
};

/** The steps before a timetable can be generated, ticked from the records. */
export function generationChecklist(periods: TimetablePeriod[], assignments: Assignment[], slots: TimetableSlot[]) {
  return [
    { label: "Set the lesson periods", done: periods.some(isLesson) },
    { label: "Assign teachers to subjects and classes", done: assignments.length > 0 },
    { label: "Generate the timetable", done: slots.length > 0 },
  ];
}

// --- Change requests -------------------------------------------------------------------

export const REQUEST_TYPES: [TimetableRequest["request_type"], string][] = [
  ["change_time", "Move to another day or period"],
  ["change_room", "Move to another room"],
  ["remove_slot", "Remove the lesson"],
  ["change_teacher", "Give to another teacher"],
  ["add_slot", "Add another lesson"],
  ["swap_slots", "Swap with another lesson"],
];
export const requestTypeLabel = (type: string) => REQUEST_TYPES.find(([value]) => value === type)?.[1] ?? humanise(type);

export const requestStatus = (request: Pick<TimetableRequest, "status">) => {
  const map: Record<string, { label: string; dot: string }> = {
    pending: { label: "Awaiting review", dot: "bg-amber-500" },
    approved: { label: "Approved, not yet made", dot: "bg-sky-500" },
    implemented: { label: "Made", dot: "bg-emerald-500" },
    rejected: { label: "Declined", dot: "bg-red-500" },
  };
  return map[request.status] ?? { label: humanise(request.status), dot: "bg-gray-400" };
};

/** What a request asks for, in words: "Wednesday, Period 2 (09:00–10:00)". */
export function requestedChangeText(request: Pick<TimetableRequest, "request_type" | "requested_changes">, periods: Map<number, TimetablePeriod>) {
  const changes = request.requested_changes ?? {};
  if (request.request_type === "change_time") {
    const day = changes.day_of_week !== undefined ? dayName(Number(changes.day_of_week)) : "";
    const period = periods.get(Number(changes.period_id ?? changes.period));
    return [day, period ? `${period.name} (${periodTime(period)})` : ""].filter(Boolean).join(", ") || "A new time";
  }
  if (request.request_type === "change_room") return changes.room ? `Room ${String(changes.room)}` : "Another room";
  if (request.request_type === "remove_slot") return "Take the lesson off the timetable";
  return requestTypeLabel(request.request_type);
}

export const requestConfig: RegisterConfig<TimetableRequest> = {
  search: (row) => [row.requested_by_name, row.current_slot_display, row.reason, row.review_notes].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "pending", label: "Awaiting review", match: (row) => row.status === "pending" },
    { key: "approved", label: "To make", match: (row) => row.status === "approved" },
    { key: "implemented", label: "Made", match: (row) => row.status === "implemented" },
    { key: "rejected", label: "Declined", match: (row) => row.status === "rejected" },
  ],
  date: { label: "Asked", value: (row) => row.created_at },
  category: { label: "Change", value: (row) => requestTypeLabel(row.request_type) },
  sorts: [
    { key: "-created", label: "Newest first", compare: reversed(byText<TimetableRequest>((row) => row.created_at)) },
    { key: "created", label: "Oldest first", compare: byText<TimetableRequest>((row) => row.created_at) },
  ],
};
